import React, { useState, useRef } from 'react';
import { useOrgChart } from '../../contexts/OrgChartContext';
import './OrgChartManager.css';

function OrgChartManager() {
  const { 
    orgNodes, 
    loading, 
    error, 
    addNode, 
    editNode, 
    deleteNode,
    getAllNodesFlat
  } = useOrgChart();
  
  const [isAdding, setIsAdding] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [parentId, setParentId] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [showChart, setShowChart] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const fileInputRef = useRef(null);
  
  // Get all nodes for parent selection dropdown
  const allNodes = getAllNodesFlat();
  
  // Flatten hierarchy for display
  const getFlattenedHierarchy = () => {
    // Create a map of id to node
    const nodeMap = {};
    allNodes.forEach(node => {
      nodeMap[node.id] = { ...node, level: 0, path: '' };
    });
    
    // Calculate levels and paths
    const result = [];
    
    // Function to process a node and its ancestors
    const processNode = (nodeId, level = 0, path = '') => {
      const node = nodeMap[nodeId];
      if (!node) return;
      
      node.level = level;
      node.path = path;
      result.push(node);
      
      // Find children
      allNodes
        .filter(n => n.parentId === nodeId)
        .sort((a, b) => a.order - b.order)
        .forEach(child => {
          processNode(child.id, level + 1, `${path}${path ? ' > ' : ''}${node.name}`);
        });
    };
    
    // Process all root nodes
    allNodes
      .filter(node => !node.parentId)
      .sort((a, b) => a.order - b.order)
      .forEach(rootNode => {
        processNode(rootNode.id);
      });
    
    return result;
  };
  
  const handleAddNewClick = () => {
    setIsAdding(true);
    setEditingNode(null);
    resetForm();
  };
  
  const handleEditClick = (node) => {
    setIsAdding(false);
    setEditingNode(node);
    setName(node.name || '');
    setTitle(node.title || '');
    setParentId(node.parentId || '');
    setImagePreview(node.imageBase64 || '');
    setImage(null);
  };
  
  const handleSaveOrganization = async () => {
    try {
      setIsSaving(true);
      setActionError('');
      
      // This is where you would save the entire organization structure
      // For now, we'll just simulate a save operation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setActionSuccess('Organization structure saved successfully!');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        if (actionSuccess === 'Organization structure saved successfully!') {
          setActionSuccess('');
        }
      }, 3000);
    } catch (err) {
      setActionError('Failed to save organization structure: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteClick = async (node) => {
    // Check if this node has children before confirming deletion
    const hasChildren = allNodes.some(n => n.parentId === node.id);
    
    if (hasChildren) {
      setActionError('Cannot delete a node with children. Please remove or reassign the children first.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${node.name}?`)) {
      setIsLoading(true);
      setActionError('');
      setActionSuccess('');
      
      try {
        await deleteNode(node.id);
        setActionSuccess(`${node.name} was deleted successfully!`);
      } catch (err) {
        setActionError(err.message || 'Failed to delete node');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const resetForm = () => {
    setName('');
    setTitle('');
    setParentId('');
    setImage(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleCancel = () => {
    setIsAdding(false);
    setEditingNode(null);
    resetForm();
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setActionError('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }
    
    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setActionError('Image file is too large. Maximum size is 2MB.');
      return;
    }
    
    setImage(file);
    
    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      setActionError('Name is required');
      return;
    }
    
    setIsLoading(true);
    setActionError('');
    setActionSuccess('');
    
    try {
      if (editingNode) {
        // Edit existing node
        await editNode(editingNode.id, { 
          name, 
          title, 
          parentId: parentId || null, 
          imageFile: image 
        });
        setActionSuccess(`${name} updated successfully!`);
      } else {
        // Add new node
        await addNode(
          name,
          title,
          parentId || null,
          image
        );
        setActionSuccess(`${name} added successfully!`);
        resetForm();
      }
      
      // Reset form after successful submission if adding new
      if (!editingNode) {
        resetForm();
        setIsAdding(false);
      }
    } catch (err) {
      setActionError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Prevent circular parent relationships
  const getValidParentOptions = () => {
    if (!editingNode) return allNodes;
    
    // Find all descendants of the editing node
    const findDescendants = (nodeId) => {
      const descendants = [];
      const children = allNodes.filter(n => n.parentId === nodeId);
      
      children.forEach(child => {
        descendants.push(child);
        descendants.push(...findDescendants(child.id));
      });
      
      return descendants;
    };
    
    const descendants = findDescendants(editingNode.id);
    const descendantIds = new Set(descendants.map(d => d.id));
    
    // Also exclude the node itself
    descendantIds.add(editingNode.id);
    
    // Return all nodes that are not descendants of the editing node
    return allNodes.filter(node => !descendantIds.has(node.id));
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="org-chart-manager">
        <h2 className="section-title">Organizational Chart Manager</h2>
        <div className="loading-indicator">Loading organizational chart data...</div>
      </div>
    );
  }
  
  // Prepare data for display
  const flattenedNodes = getFlattenedHierarchy();
  const validParentOptions = getValidParentOptions();
  
  return (
    <div className="org-chart-manager">
      <h2 className="section-title">Organizational Chart Manager</h2>
      
      {error && <div className="error-message">{error}</div>}
      {actionError && <div className="error-message">{actionError}</div>}
      {actionSuccess && <div className="success-message">{actionSuccess}</div>}
      
      <div className="org-chart-controls">
        {!isAdding && !editingNode && (
          <>
            <button 
              className="add-node-btn"
              onClick={handleAddNewClick}
              disabled={isLoading || isSaving}
            >
              Add New Position
            </button>
            
            <button
              className="save-org-btn"
              onClick={handleSaveOrganization}
              disabled={isLoading || isSaving || allNodes.length === 0}
              style={{
                background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '0.9rem 2rem',
                fontWeight: '700',
                fontSize: '1.05rem',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(56, 239, 125, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isSaving ? 'Saving...' : 'SAVE ORGANIZATION'}
              <span style={{ fontSize: '1.2rem' }}>💾</span>
            </button>
          </>
        )}
        
        <button 
          className={`view-toggle-btn ${showChart ? 'active' : ''}`}
          onClick={() => setShowChart(!showChart)}
        >
          {showChart ? 'Show List View' : 'Show Chart View'}
        </button>
      </div>
      
      {(isAdding || editingNode) && (
        <div className="node-form-container">
          <h3>{editingNode ? 'Edit Position' : 'Add New Position'}</h3>
          
          <form onSubmit={handleSubmit} className="node-form">
            <div className="form-group">
              <label htmlFor="name">Name <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter person's name"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="title">Title/Position</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter position title"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="parentId">Reports To</label>
              <select
                id="parentId"
                value={parentId || ''}
                onChange={(e) => setParentId(e.target.value || null)}
              >
                <option value="">-- No Superior (Top Level) --</option>
                {validParentOptions.map(node => (
                  <option key={node.id} value={node.id}>
                    {node.name} {node.title ? `(${node.title})` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Photo (Optional)</label>
              
              <div className="image-upload-controls">
                <input
                  type="file"
                  id="person-image"
                  ref={fileInputRef}
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  onChange={handleImageChange}
                  className="image-file-input"
                />
                <label htmlFor="person-image" className="image-upload-button">
                  {editingNode && editingNode.imageBase64 ? 'Change Photo' : 'Upload Photo'}
                </label>
                
                {imagePreview && (
                  <button
                    type="button"
                    className="image-remove-button"
                    onClick={() => {
                      setImage(null);
                      setImagePreview('');
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
              
              {imagePreview && (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                </div>
              )}
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn save-position-btn"
                disabled={isLoading || !name.trim()}
                style={{
                  background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  padding: '1rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                {isLoading ? 'Processing...' : editingNode ? 'UPDATE POSITION' : 'SAVE POSITION'}
                <span style={{ 
                  position: 'absolute',
                  right: '15px',
                  fontSize: '1.2rem'
                }}>
                  💾
                </span>
              </button>
            </div>

            {/* Clear Register Button */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button 
                type="submit"
                className="register-position-btn" 
                style={{
                  background: 'linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '1.2rem 2.5rem',
                  fontWeight: '700',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(255, 75, 43, 0.3)',
                  width: '100%',
                  maxWidth: '400px',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
                disabled={isLoading || !name.trim()}
              >
                <span style={{ fontSize: '1.4rem' }}>✅</span>
                REGISTER {editingNode ? 'CHANGES' : 'POSITION'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {showChart ? (
        <div className="org-chart-display">
          {flattenedNodes.length === 0 ? (
            <p className="no-data-message">No positions defined yet. Add your first position to start building the organizational chart.</p>
          ) : (
            <div className="org-chart">
              {renderOrgChart(allNodes)}
            </div>
          )}
        </div>
      ) : (
        <div className="org-list-view">
          <h3>Organizational Structure</h3>
          
          {flattenedNodes.length === 0 ? (
            <p className="no-data-message">No positions defined yet. Add your first position to start building the organizational chart.</p>
          ) : (
            <table className="org-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title/Position</th>
                  <th>Reports To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flattenedNodes.map(node => {
                  const parentNode = node.parentId ? allNodes.find(n => n.id === node.parentId) : null;
                  
                  return (
                    <tr key={node.id} className={`level-${node.level}`}>
                      <td>
                        <div className="org-table-name">
                          {node.imageBase64 && (
                            <div className="org-table-image">
                              <img src={node.imageBase64} alt={node.name} />
                            </div>
                          )}
                          <span>{node.name}</span>
                        </div>
                      </td>
                      <td>{node.title || 'N/A'}</td>
                      <td>{parentNode ? parentNode.name : 'None (Top Level)'}</td>
                      <td>
                        <div className="org-table-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditClick(node)}
                            disabled={isLoading}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteClick(node)}
                            disabled={isLoading}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Final Save Organization button at the bottom */}
      {!isAdding && !editingNode && allNodes.length > 0 && (
        <div className="save-org-container" style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '2.5rem',
          marginBottom: '1rem'
        }}>
          <button
            className="save-org-btn-large"
            onClick={handleSaveOrganization}
            disabled={isSaving}
            style={{
              background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '1.2rem 3rem',
              fontWeight: '700',
              fontSize: '1.2rem',
              cursor: 'pointer',
              boxShadow: '0 10px 25px rgba(56, 239, 125, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'all 0.3s ease'
            }}
          >
            {isSaving ? 'Saving Organization...' : 'SAVE ORGANIZATION CHART'}
            <span style={{ fontSize: '1.5rem' }}>💾</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Helper function to render the hierarchical org chart
function renderOrgChart(allNodes) {
  // Map for faster lookups
  const nodeMap = {};
  allNodes.forEach(node => {
    nodeMap[node.id] = { ...node, children: [] };
  });
  
  // Build tree structure
  const rootNodes = [];
  allNodes.forEach(node => {
    if (node.parentId) {
      if (nodeMap[node.parentId]) {
        nodeMap[node.parentId].children.push(nodeMap[node.id]);
      }
    } else {
      rootNodes.push(nodeMap[node.id]);
    }
  });
  
  // Sort children by their order
  const sortChildren = (node) => {
    if (node.children && node.children.length > 0) {
      node.children.sort((a, b) => a.order - b.order);
      node.children.forEach(sortChildren);
    }
    return node;
  };
  
  rootNodes.sort((a, b) => a.order - b.order);
  rootNodes.forEach(sortChildren);
  
  // Render tree recursively
  const renderNode = (node) => {
    return (
      <div key={node.id} className="org-chart-node">
        <div className="org-node-content">
          {node.imageBase64 && (
            <div className="org-node-image">
              <img src={node.imageBase64} alt={node.name} />
            </div>
          )}
          <div className="org-node-details">
            <h4>{node.name}</h4>
            {node.title && <p>{node.title}</p>}
          </div>
        </div>
        
        {node.children && node.children.length > 0 && (
          <div className="org-chart-children">
            {node.children.map(child => renderNode(child))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="org-chart-tree">
      {rootNodes.map(node => renderNode(node))}
    </div>
  );
}

export default OrgChartManager; 